import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { useFocusTrap } from '@/shared/hooks/useFocusTrap';
import { usePresence } from '@/shared/hooks/usePresence';

export interface DrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  side?: 'start' | 'end';
  width?: number;
  children: ReactNode;
  footer?: ReactNode;
}

export function Drawer({
  isOpen, onClose, title, side = 'end', width = 480, children, footer,
}: DrawerProps) {
  const titleId = useId();
  const trapRef = useFocusTrap(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const { mounted, leaving, content } = usePresence(isOpen, children);
  if (!mounted) return null;

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        animation: leaving ? 'gcFadeOut .22s ease both' : 'gcFade .3s ease both',
        pointerEvents: leaving ? 'none' : undefined,
        zIndex: 999,
      }}
    >
      <aside
        ref={trapRef as React.RefObject<HTMLElement>}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          // מגירה "צפה" (עיצוב v2): מרווח מקצוות המסך ופינות מעוגלות.
          position: 'absolute',
          top: 12,
          bottom: 12,
          [side === 'end' ? 'insetInlineEnd' : 'insetInlineStart']: 12,
          width,
          maxWidth: 'calc(100vw - 24px)',
          background: 'var(--surface)',
          border: '1px solid var(--glass-line)',
          borderRadius: 'var(--r-4)',
          overflow: 'hidden',
          boxShadow: 'inset 0 1px 0 var(--glass-hi), var(--shadow-3)',
          display: 'flex',
          flexDirection: 'column',
          animation: leaving
            ? 'gcDrawerOut .22s cubic-bezier(.4,0,1,1) both'
            : 'gcDrawer .45s cubic-bezier(.2,.8,.2,1) both',
        }}
      >
        {title && (
          <header
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--pad-card)',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <h2 id={titleId} style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: -0.3 }}>{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="סגור"
              className="btn ghost close-spin"
              style={{ height: 34, width: 34, padding: 0, borderRadius: 99, background: 'var(--surface-3)' }}
            >
              <Icon name="close" size={18} />
            </button>
          </header>
        )}
        <div style={{ flex: 1, overflow: 'auto', padding: 'var(--pad-card)' }}>{content}</div>
        {footer && (
          <footer
            style={{
              padding: 'var(--pad-card)',
              borderTop: '1px solid var(--line)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            {footer}
          </footer>
        )}
      </aside>
    </div>,
    document.body
  );
}
